import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api, formatDate, money } from "@/lib/api";
import { Users, Building2, FolderKanban, FileText, Send, FileSignature, Receipt, MessageSquare, Mail, ArrowRight } from "lucide-react";

const KPI = ({ icon: Icon, label, value }) => (
  <div className="jc-card p-5">
    <div className="flex items-center justify-between">
      <div className="overline">{label}</div>
      <Icon size={16} className="text-[var(--jc-text-soft)]" />
    </div>
    <div className="font-display text-3xl mt-2">{value}</div>
  </div>
);

const Admin = () => {
  const [overview, setOverview] = useState({});
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [projects, setProjects] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [invoices, setInvoices] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/admin/overview"), api.get("/admin/users"), api.get("/admin/companies"),
      api.get("/projects"), api.get("/estimates"), api.get("/proposals"),
      api.get("/contracts"), api.get("/invoices"),
    ]).then(([o, u, c, p, e, pr, co, i]) => {
      setOverview(o.data); setUsers(u.data); setCompanies(c.data);
      setProjects(p.data); setEstimates(e.data); setProposals(pr.data);
      setContracts(co.data); setInvoices(i.data);
    });
  }, []);

  return (
    <DashboardLayout title="Admin" subtitle="All users, companies, and activity across JobClose AI.">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI icon={Users} label="Users" value={overview.users || 0} />
        <KPI icon={Building2} label="Contractors" value={overview.contractors || 0} />
        <KPI icon={FolderKanban} label="Projects" value={overview.projects || 0} />
        <KPI icon={FileText} label="Estimates" value={overview.estimates || 0} />
        <KPI icon={Send} label="Proposals" value={overview.proposals || 0} />
        <KPI icon={FileSignature} label="Contracts" value={overview.contracts || 0} />
        <KPI icon={Receipt} label="Invoices" value={overview.invoices || 0} />
        <KPI icon={MessageSquare} label="Updates" value={overview.customer_updates || 0} />
      </div>

      <div className="mt-6">
        <Link
          to="/app/admin/email-outbox"
          data-testid="admin-email-outbox-link"
          className="jc-card p-4 inline-flex items-center gap-3 hover:bg-[var(--jc-bg)] transition-colors"
        >
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-[var(--jc-blue)]/10 text-[var(--jc-blue)]">
            <Mail size={18} />
          </span>
          <span>
            <span className="block font-semibold text-sm">Email Outbox</span>
            <span className="block text-xs text-[var(--jc-text-soft)]">Read-only ledger of every queued, mocked, or sent email.</span>
          </span>
          <ArrowRight size={14} className="ml-2 text-[var(--jc-text-soft)]" />
        </Link>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-8">
        <Table title="Users" rows={users} cols={[
          { label: "Name", get: (r) => r.full_name },
          { label: "Email", get: (r) => r.email },
          { label: "Role", get: (r) => r.role },
          { label: "Joined", get: (r) => formatDate(r.created_at) },
        ]} />
        <Table title="Contractor Companies" rows={companies} cols={[
          { label: "Company", get: (r) => r.company_name },
          { label: "Owner", get: (r) => r.full_name },
          { label: "Email", get: (r) => r.email },
          { label: "Joined", get: (r) => formatDate(r.created_at) },
        ]} />
        <Table title="All Projects" rows={projects} cols={[
          { label: "Customer", get: (r) => r.customer_name },
          { label: "Job", get: (r) => r.job_type || "—" },
          { label: "Address", get: (r) => r.job_address || "—" },
        ]} />
        <Table title="All Invoices" rows={invoices} cols={[
          { label: "Customer", get: (r) => r.snapshot?.customer?.name || "—" },
          { label: "Type", get: (r) => r.invoice_type },
          { label: "Amount", get: (r) => money(r.amount) },
          { label: "Status", get: (r) => r.payment_status },
        ]} />
        <Table title="All Estimates" rows={estimates} cols={[
          { label: "Project", get: (r) => r.project_id?.slice(0, 8) },
          { label: "Lines", get: (r) => r.line_items?.length || 0 },
          { label: "Total", get: (r) => money(r.total) },
          { label: "Status", get: (r) => r.status },
        ]} />
        <Table title="All Contracts" rows={contracts} cols={[
          { label: "Customer", get: (r) => r.snapshot?.customer?.name || "—" },
          { label: "Status", get: (r) => r.status },
          { label: "Total", get: (r) => money(r.snapshot?.totals?.total) },
        ]} />
        <Table title="All Proposals" rows={proposals} cols={[
          { label: "Customer", get: (r) => r.snapshot?.customer?.name || "—" },
          { label: "Status", get: (r) => r.status },
          { label: "Total", get: (r) => money(r.snapshot?.totals?.total) },
        ]} />
        <div className="jc-card p-6">
          <div className="overline mb-2">Subscriptions</div>
          <div className="text-sm text-[var(--jc-text-soft)]">Stripe live billing connects on launch. Subscription status is tracked as a placeholder for each contractor.</div>
        </div>
      </div>
    </DashboardLayout>
  );
};

const Table = ({ title, rows, cols }) => (
  <div className="jc-card overflow-hidden">
    <div className="px-5 py-3 border-b border-[var(--jc-border)] bg-[var(--jc-bg)] overline">{title} ({rows.length})</div>
    {rows.length === 0 ? (
      <div className="p-5 text-sm text-[var(--jc-text-soft)]">No data.</div>
    ) : (
      <div className="max-h-72 overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-white text-[var(--jc-text-soft)] sticky top-0">
            <tr>{cols.map((c) => <th key={c.label} className="text-left px-5 py-2.5 font-semibold">{c.label}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-[var(--jc-border)]">
            {rows.slice(0, 50).map((r, i) => (
              <tr key={i} className="hover:bg-[var(--jc-bg)]">
                {cols.map((c) => <td key={c.label} className="px-5 py-2">{c.get(r) || "—"}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

export default Admin;
